// The download page's one decision: what the primary action is for the machine you're on, and what every system's state is.
// Pure, so the tests can pin every case. The release state is not forked here: builds and their states come from
// /downloads/latest.txt (downloads.ts, the list release-ops publishes). Never a date (owner, wave 13).
//
//   macOS, Linux  the confirmed build with its honest state (release, early, test), else "not out yet", no link;
//   Windows       the waiting list (an email, no account) until a build is listed;
//   phones        "Play in browser" and a note to open this page on a computer: never a download button.

import type { Offer } from "../../downloads.ts";
import type { Platform } from "../../play.ts";
import { dateMs, type Zone } from "../spec.ts";

export type GatePlatform = Platform; // "mac" | "windows" | "linux" | "mobile" | "other"

/** The platforms `?platform=` may force (the screenshot harness can't fake a phone's user agent). */
const FORCEABLE: GatePlatform[] = ["mac", "windows", "linux", "mobile", "other"];

/**
 * The visitor's platform. iPadOS Safari sends a Mac user agent, so a "Mac" with a touch screen is a tablet: it gets the
 * phone treatment. `override` is `?platform=` (for screenshots and checking each state by hand).
 */
export function gatePlatform(ua: string, maxTouchPoints: number, override: string | null, detect: (ua: string) => Platform): GatePlatform {
  if (override && (FORCEABLE as string[]).includes(override)) return override as GatePlatform;
  const p = detect(ua);
  if (p === "mac" && maxTouchPoints > 1) return "mobile";
  return p;
}

export type GateAction = { kind: "download"; offer: Offer } | { kind: "not-yet"; offer: Offer } | { kind: "waitlist"; offer: Offer } | { kind: "browser" };

/** The primary action. "other" (an unknown desktop) gets the first system with a build, else the browser. */
export function gateAction(platform: GatePlatform, all: Offer[]): GateAction {
  if (platform === "mobile") return { kind: "browser" };
  const offer = platform === "other" ? all.find((o) => o.builds.length > 0) : all.find((o) => o.platform === platform);
  if (!offer) return { kind: "browser" };
  if (offer.builds.length > 0) return { kind: "download", offer };
  if (offer.platform === "windows") return { kind: "waitlist", offer };
  return { kind: "not-yet", offer };
}

/**
 * A download's label: never the package file name (it carries build numbers). One file says the system; the Linux
 * pair says which is which, since both are needed.
 */
export function buildLabel(o: Offer, url: string): string {
  if (o.builds.length === 1) return `Download for ${o.label}`;
  const name = url.slice(url.lastIndexOf("/") + 1);
  if (/^banger-runtime[_-]/.test(name)) return "Runtime (needed)";
  if (/^banger[_-]/.test(name)) return "Banger app";
  return `${o.label} download`;
}

/** The Gate's questions for the builds that are out: Windows answers appear once a Windows installer is listed. */
export function gateQuestions(all: Offer[], freeAnswer: string): [string, string][] {
  const win = all.some((o) => o.platform === "windows" && o.builds.length > 0);
  const script = all.some(usesInstaller);
  return [
    ["Is Banger free?", freeAnswer],
    ["Do I need the app to play?", "No. Every game page has a Play in browser link that opens the game on its own site. The app is where your library, friends and parties live."],
    ["Do I need an account?", "No, you can play without one. Sign in to keep your library and progress across computers, add friends and join parties."],
    [
      "My Mac says Banger can't be opened. What now?",
      "Banger is notarised by Apple, so it opens without a warning. If an older copy still warns, update to the latest from this page. On macOS 13 and 14 you can also Control-click Banger in Applications and choose Open; on macOS 15 and later, open it once, then click Open Anyway in System Settings, Privacy & Security.",
    ],
    [
      "Record says Banger needs Screen Recording, but it's already on. What now?",
      "This can happen once, after updating from Banger 0.1.4 or earlier on a Mac. In System Settings, Privacy & Security, Screen & System Audio Recording, remove Banger with the minus button (or switch it off and on again). Then press F10 in a game, allow it and reopen the game. Later updates keep the permission.",
    ],
    ...(script
      ? ([
          [
            "Is the install command safe to run?",
            "Piping a script into your terminal means trusting it, so read it first: it's short, at banger.sh/install.sh. It only downloads from banger.sh over https, refuses any file whose SHA-256 doesn't match the one published next to it, and never asks for your password. On a Mac it puts Banger in Applications; on Linux it prints the apt command for you to run.",
          ],
        ] as [string, string][])
      : []),
    ...(win
      ? ([
          [
            "Windows says \"Windows protected your PC\". What now?",
            "The installer isn't signed with a certificate Windows recognises yet, so Windows warns the first time you run it. Click More info, then Run anyway. You only do this once. To check the file first, compare its SHA-256 with the one under Requirements and checksums.",
          ],
          ["What about phones?", "On a phone, every game plays in your browser."],
        ] as [string, string][])
      : ([["What about Windows and phones?", "Windows isn't out yet: join the waiting list and we'll email you once, the day it's out. On a phone, every game plays in your browser."]] as [string, string][])),
    [
      "How do I uninstall it?",
      `On a Mac, move Banger from Applications to the Bin. On Linux, remove the banger and banger-runtime packages.${win ? " On Windows, open Settings, then Apps, find Banger and choose Uninstall." : ""}`,
    ],
  ];
}

/**
 * The one-line installer, the primary action on macOS and Linux (owner, wave 13): curl doesn't mark the download, so
 * macOS never shows its unidentified-developer prompt, and install.sh checks every SHA-256. Always production:
 * install.sh's own default site is https://banger.sh too.
 */
export const INSTALL_COMMAND = "curl -fsSL https://banger.sh/install.sh | sh";

/** Whether install.sh installs this system's builds: the macOS app and the Linux packages, not Windows. */
export function usesInstaller(o: Offer): boolean {
  return o.builds.length > 0 && (o.platform === "mac" || o.platform === "linux");
}

/** What the command does, in one line. */
export function installerLine(o: Offer): string {
  return o.platform === "mac"
    ? "Paste into Terminal. Checks the SHA-256, installs to Applications."
    : "Paste into a terminal. Checks the SHA-256, then prints the apt command.";
}

/** A system's state as the page says it: a badge and one honest line. */
export interface PlatformState {
  badge: string;
  tone: "ok" | "early" | "warn" | "none";
  note: string;
}

export function platformState(o: Offer): PlatformState {
  switch (o.state) {
    case "release":
      // the macOS app updates itself; Linux packages are updated by apt, so the app only says when one is out
      return { badge: "Available", tone: "ok", note: o.platform === "linux" ? "The full app. It tells you when there's an update." : "The full app. It keeps itself up to date." };
    case "early":
      // early: on the stable update channel, but not through every check yet (Windows 0.1.3: tested in a VM so far)
      return { badge: "Beta", tone: "early", note: "Beta. It tells you when there's an update." };
    case "test":
      return {
        badge: "Test build",
        tone: "warn",
        note: "A test build on our test servers: your account isn't in it. Install the full release over it when it's out.",
      };
    case "none":
      return o.platform === "windows"
        ? { badge: "Waiting list", tone: "none", note: "No Windows build yet. Leave your email and we'll write once, the day it's out." }
        : { badge: "Not out yet", tone: "none", note: `No ${o.label} build yet. The download appears here the day it's out.` };
  }
}

/** The other systems, as the line under the primary action names them. */
export function otherOffers(action: GateAction, all: Offer[]): Offer[] {
  return action.kind === "browser" ? all : all.filter((o) => o !== action.offer);
}

/** A moment's time in the viewer's own zone, e.g. "Sat 7 Nov, 22:00 EET". */
export function localMoment(atMs: number, locale?: string, timeZone?: string): string {
  return new Intl.DateTimeFormat(locale ?? "en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZoneName: "short", timeZone }).format(atMs);
}

/**
 * What a cross-promo says here. Open Haunt's Plaza night happens in the app: on a computer the banner says when, in the
 * viewer's time; on a phone (where the app can't be installed) it invites them to play the haunts in the browser.
 */
export function promoCopy(
  target: Pick<Zone, "id" | "tagline" | "moments">,
  specCopy: string | undefined,
  phone: boolean,
  fmt: (ms: number) => string = (ms) => localMoment(ms),
  canInstall = true,
): string | undefined {
  if (target.id !== "open-haunt") return specCopy;
  if (phone) return "Spooky games from creators everywhere, free to play in your browser tonight.";
  const night = target.moments.find((m) => m.id === "plaza-night");
  const at = night ? dateMs(night.at) : NaN;
  if (!Number.isFinite(at)) return specCopy;
  // "get it" only where there's something to get (no build for this computer yet: the haunts play in the browser)
  return `Haunt night in the Plaza happens in the app: ${fmt(at)}. ${canInstall ? "Get it before then." : "Until the app is out for your computer, the haunts play in your browser."}`;
}
